/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useState } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { SURFACE_CARD_RADIUS_CONTENT } from '../../../shared/surface-cards/surfaceCards.styles';
import { useModalBackdropPadding } from '../../../shared/ui/hooks-ui/useModalBackdropPadding';
import type { BranchSalesByProductRow, SalesByProductSortKey } from '../../domain/reportsTypes';
import { getCategoryDisplayLabel, normalizeProductCategoryId } from '../../../catalog/domain-api';
import { styles as reportStyles } from '../ReportsScreen.styles';
import { ReportsDenseListCard } from './ReportsDenseListCard';
import { OperationalFilterChipGroup } from '../../../shared/filters/OperationalFilterPrimitives';
import { OperationalHintIcon } from '../../../shared/ui/OperationalHintIcon';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { formatQty } from '@shared/quantity';
import { formatMoney } from '../../../shared/format/public-api';
import {
  abcClassColor,
  avgUnitPrice,
  computeAbcSeries,
  formatMoney0,
  formatMoney2,
  formatPct1,
  pctOfTotal,
  unitsPerDay,
} from '../salesByProductMetrics';

/** Ancho mínimo del contenido de la tabla (scroll horizontal si no entra el viewport útil). */
const TABLE_CONTENT_MIN_WIDTH = 720;

/** Resta a `useWindowDimensions().width`: padding del Informes + shell denso (~content.lg×2 + ~section.md×2). */
const TABLE_VIEWPORT_GUTTER = 88;

/**
 * A partir de este ancho de ventana se usa el layout tipo tabla; por debajo, cards (móvil, web angosto, etc.).
 * Coincide en orden de magnitud con TABLE_CONTENT_MIN_WIDTH para que la tabla no se sienta forzada en pantallas muy estrechas.
 */
const LAYOUT_USE_TABLE_MIN_WINDOW_WIDTH = 720;

/** Nombre de ítem para mostrar: el RPC puede devolver label vacío (producto borrado/legado). */
function displayItemLabel(row: BranchSalesByProductRow): string {
  const l = (row.label ?? '').trim();
  if (l) return l;
  return row.sku?.trim() || 'Ítem sin nombre';
}

const SORT_OPTIONS: { key: SalesByProductSortKey; label: string; shortHint: string }[] = [
  { key: 'quantity_desc', label: 'Unidades', shortHint: 'Ordenar por unidades vendidas' },
  { key: 'line_total_desc', label: 'Importe', shortHint: 'Ordenar por importe' },
  { key: 'label_asc', label: 'Nombre', shortHint: 'Orden alfabético por nombre' },
];

function showCategoryBadgeInListing(filterCategorySlug: string | null, rowCategorySlug: string | null | undefined): boolean {
  if (!rowCategorySlug) return false;
  if (!filterCategorySlug) return true;
  return normalizeProductCategoryId(filterCategorySlug) !== normalizeProductCategoryId(rowCategorySlug);
}

type RowsProps = {
  rows: BranchSalesByProductRow[];
  pageIndex: number;
  pageSize: number;
  sumLineTotal: number;
  sumQuantity: number;
  daysInRange: number;
  sort: SalesByProductSortKey;
  onSortChange: (next: SalesByProductSortKey) => void;
  filterCategory: string | null;
  /** Cumulative lineTotal of all rows in pages before the current one (for ABC calculation). */
  abcCumulativeBefore?: number;
};

type LayoutProps = RowsProps & {
  onOpenDetail: (row: BranchSalesByProductRow, rank: number) => void;
};

function SalesByProductSortChips({
  sort,
  onSortChange,
}: {
  sort: SalesByProductSortKey;
  onSortChange: (next: SalesByProductSortKey) => void;
}) {
  return (
    <View style={{ marginBottom: spacing.sm }}>
      <OperationalFilterChipGroup
        options={SORT_OPTIONS.map((o) => ({
          value: o.key,
          label: o.label,
          accessibilityLabel: o.shortHint,
        }))}
        value={sort}
        onChange={onSortChange}
      />
    </View>
  );
}

export function ReportsSalesByProductRows(props: RowsProps) {
  const { width, height: windowHeight } = useWindowDimensions();
  const useTableLayout = width >= LAYOUT_USE_TABLE_MIN_WINDOW_WIDTH;
  const isMobile = width < 768;
  const insets = useSafeAreaInsets();
  /** Alto útil para el detalle: suficiente para los ~9 campos sin scroll en pantallas normales. */
  const modalScrollMaxHeight = Math.max(320, Math.round(windowHeight * (isMobile ? 0.62 : 0.72)));
  const { sort, onSortChange } = props;
  // El modal de detalle vive acá para que ambos layouts (tabla desktop y cards
  // angostas) puedan abrirlo — antes solo existía en cards (#drilldown).
  const [detailModal, setDetailModal] = useState<ProductDetailModalPayload | null>(null);
  const modalBackdropPadding = useModalBackdropPadding();
  const openDetail = (row: BranchSalesByProductRow, rank: number) => setDetailModal({ row, rank });
  const closeModal = () => setDetailModal(null);

  const { rows, pageIndex, pageSize, sumLineTotal, daysInRange, filterCategory, abcCumulativeBefore = 0 } = props;
  const abcStartPct = sumLineTotal > 0 ? (abcCumulativeBefore / sumLineTotal) * 100 : 0;
  const abcSeries = computeAbcSeries(rows, sumLineTotal, abcStartPct);
  const modalRow = detailModal?.row;
  const modalRank = detailModal?.rank;
  const modalAbc =
    modalRow && modalRank != null ? abcSeries[modalRank - 1 - pageIndex * pageSize] : null;
  const modalPct$ = modalRow ? pctOfTotal(modalRow.lineTotal, sumLineTotal) : null;
  const modalAvg = modalRow ? avgUnitPrice(modalRow.lineTotal, modalRow.quantity) : null;
  const modalUpd = modalRow ? unitsPerDay(modalRow.quantity, daysInRange) : null;
  const modalShowCat =
    modalRow?.categoryId &&
    showCategoryBadgeInListing(filterCategory, modalRow.categoryId ?? null);
  const modalCatDisplay = modalShowCat ? getCategoryDisplayLabel(modalRow.categoryId) : null;
  const modalTypeLabel = modalRow?.itemType === 'service' ? 'Servicio' : 'Producto';

  return (
    <>
      {!useTableLayout ? (
        <SalesByProductSortChips sort={sort} onSortChange={onSortChange} />
      ) : null}
      {useTableLayout ? (
        <SalesByProductTable {...props} onOpenDetail={openDetail} />
      ) : (
        <SalesByProductNativeCards {...props} onOpenDetail={openDetail} />
      )}

      <Modal
        visible={detailModal != null}
        transparent
        animationType={isMobile ? 'slide' : 'fade'}
        onRequestClose={closeModal}
        accessibilityViewIsModal
      >
        <Pressable
          style={[reportStyles.modalBackdrop, isMobile ? { justifyContent: 'flex-end' } : null, isMobile ? null : modalBackdropPadding]}
          onPress={closeModal}
          accessibilityLabel="Cerrar panel al tocar afuera"
        >
          <Pressable
            style={isMobile ? reportStyles.reportsModalSheetCard : reportStyles.reportsModalDensePressable}
            onPress={(e) => e.stopPropagation()}
            accessibilityViewIsModal
          >
            {isMobile ? <View style={reportStyles.reportsModalGrabber} /> : null}
            {modalRow != null && modalRank != null ? (
              <ReportsDenseListCard
                wrapStyle={{
                  maxHeight: Platform.OS === 'web' ? ('94%' as const) : ('96%' as const),
                  width: '100%' as const,
                  minWidth: 280,
                }}
                bodyStyle={{
                  paddingVertical: spacing.md,
                  paddingHorizontal: spacing.lg,
                  gap: spacing.sm,
                  ...(isMobile ? { paddingBottom: Math.max(insets.bottom, spacing.sm) } : {}),
                }}
              >
                <View style={[reportStyles.modalHeader, { alignSelf: 'stretch' }]}>
                  <Text
                    style={[reportStyles.modalTitle, { flex: 1, minWidth: 0, paddingRight: spacing.sm }]}
                    numberOfLines={3}
                  >
                    {modalRank}. {displayItemLabel(modalRow)}
                  </Text>
                  <Pressable
                    onPress={closeModal}
                    accessibilityLabel="Cerrar detalle"
                    accessibilityRole="button"
                    hitSlop={12}
                  >
                    <Ionicons name="close" size={24} color={ticketColors.ink} />
                  </Pressable>
                </View>
                <ScrollView
                  style={{ alignSelf: 'stretch', width: '100%', maxHeight: modalScrollMaxHeight, flexGrow: 0, flexShrink: 0 }}
                  keyboardShouldPersistTaps="handled"
                  showsVerticalScrollIndicator
                  contentContainerStyle={{ width: '100%' }}
                >
                  <NativeFieldRow label="Tipo" value={modalTypeLabel} />
                  {modalCatDisplay ? <NativeFieldRow label="Categoría" value={modalCatDisplay} /> : null}
                  {modalRow.sku ? <NativeFieldRow label="Código" value={modalRow.sku} /> : null}
                  {modalAbc ? (
                    <NativeFieldRow
                      label="Clase ABC"
                      value={`${modalAbc.abcClass} (${formatPct1(modalAbc.cumulativePct)} acum.)`}
                    />
                  ) : null}
                  <NativeFieldRow label="Cantidad vendida" value={formatQty(modalRow.quantity, modalRow.unitOfMeasure)} />
                  <NativeFieldRow label="Ventas" value={formatMoney(modalRow.lineTotal)} />
                  {modalUpd != null ? (
                    <NativeFieldRow label="Promedio diario" value={`${formatQty(modalUpd, modalRow.unitOfMeasure)}/día`} />
                  ) : null}
                  <NativeFieldRow
                    label="Participación en $"
                    value={modalPct$ != null ? formatPct1(modalPct$) : '—'}
                  />
                  <NativeFieldRow
                    label="Precio medio"
                    value={modalAvg != null ? `${formatMoney(modalAvg)}/u.` : '—'}
                  />
                </ScrollView>
              </ReportsDenseListCard>
            ) : null}
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

function SalesByProductTable({
  rows,
  pageIndex,
  pageSize,
  sumLineTotal,
  sumQuantity,
  sort,
  onSortChange,
  filterCategory,
  abcCumulativeBefore = 0,
  onOpenDetail,
}: LayoutProps) {
  const { width: windowWidth } = useWindowDimensions();
  /** En PC la tabla usa todo el ancho útil; en ventanas angostas se mantiene mínimo y scroll horizontal */
  const tableCanvasWidth = Math.max(TABLE_CONTENT_MIN_WIDTH, windowWidth - TABLE_VIEWPORT_GUTTER);
  const needsHorizontalScroll = windowWidth <= tableCanvasWidth + 8;

  const abcStartPct = sumLineTotal > 0 ? (abcCumulativeBefore / sumLineTotal) * 100 : 0;
  const abcSeries = computeAbcSeries(rows, sumLineTotal, abcStartPct);

  const tableInner = (
    <>
      <View style={[tableStyles.webRow, tableStyles.webHeader]}>
        <View style={tableStyles.colRank}>
          <Text style={[tableStyles.webHeaderText, { textAlign: 'right' }]}>#</Text>
        </View>
        <View style={[tableStyles.colAbc, { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 2 }]}>
          <Text style={[tableStyles.webHeaderText, { textAlign: 'center' }]}>ABC</Text>
          <OperationalHintIcon
            severity="info"
            size={13}
            title="Clasificación ABC"
            message="A = productos que generan el 80% del importe. B = del 80% al 95%. C = el 5% restante. Ayuda a priorizar stock y negociación."
          />
        </View>
        <Pressable
          style={tableStyles.colProduct}
          onPress={() => onSortChange('label_asc')}
          accessibilityRole="button"
          accessibilityLabel="Ordenar por nombre del ítem, A–Z"
        >
          <Text style={[tableStyles.webHeaderText, { textAlign: 'left' }, sort === 'label_asc' && tableStyles.webHeaderSortActive]}>
            Ítem
          </Text>
        </Pressable>
        <View style={tableStyles.colSku}>
          <Text style={[tableStyles.webHeaderText, { textAlign: 'left' }]}>SKU</Text>
        </View>
        <Pressable
          style={tableStyles.colQty}
          onPress={() => onSortChange('quantity_desc')}
          accessibilityRole="button"
          accessibilityLabel="Ordenar por unidades vendidas"
        >
          <Text style={[tableStyles.webHeaderText, sort === 'quantity_desc' && tableStyles.webHeaderSortActive]}>
            Cant.
          </Text>
        </Pressable>
        <Pressable
          style={tableStyles.colMoney}
          onPress={() => onSortChange('line_total_desc')}
          accessibilityRole="button"
          accessibilityLabel="Ordenar por importe"
        >
          <Text style={[tableStyles.webHeaderText, sort === 'line_total_desc' && tableStyles.webHeaderSortActive]}>
            Importe
          </Text>
        </Pressable>
        <View style={tableStyles.colPct}>
          <Text style={tableStyles.webHeaderText} accessibilityLabel="% del importe total acumulado en este listado">
            {'% Importe'}
          </Text>
        </View>
        <View style={tableStyles.colPct}>
          <Text style={tableStyles.webHeaderText} accessibilityLabel="% del total de unidades en este listado">
            {'% Unid.'}
          </Text>
        </View>
        <View style={tableStyles.colAvg}>
          <Text style={tableStyles.webHeaderText} accessibilityLabel="Precio medio por unidad vendida">
            {'$/unid.'}
          </Text>
        </View>
        <View style={tableStyles.colDetail}>
          <Text style={tableStyles.webHeaderText}> </Text>
        </View>
      </View>
      {rows.map((row, idx) => {
          const rank = pageIndex * pageSize + idx + 1;
          const abc = abcSeries[idx];
          const pct$ = pctOfTotal(row.lineTotal, sumLineTotal);
          const pctU = pctOfTotal(row.quantity, sumQuantity);
          const avg = avgUnitPrice(row.lineTotal, row.quantity);
          const catHuman =
            row.categoryId && showCategoryBadgeInListing(filterCategory, row.categoryId)
              ? getCategoryDisplayLabel(row.categoryId)
              : null;
          const typeLabel = row.itemType === 'service' ? 'Servicio' : 'Producto';
          const baseLabel = displayItemLabel(row);
          const label = catHuman ? `${baseLabel} (${catHuman})` : baseLabel;
          return (
            <Pressable
              key={`${row.productId}-${idx}`}
              style={(state) => [
                tableStyles.webRow,
                (state.pressed || (state as { hovered?: boolean }).hovered) ? tableStyles.webRowHover : null,
                ...(Platform.OS === 'web' ? ([{ cursor: 'pointer' }] as object[]) : []),
              ]}
              onPress={() => onOpenDetail(row, rank)}
              accessibilityRole="button"
              accessibilityLabel={`Fila ${rank}, ${baseLabel}, ${formatQty(row.quantity, row.unitOfMeasure)}, ${formatMoney0(row.lineTotal)} pesos. Abrir detalle`}
            >
              <View style={tableStyles.colRank}>
                <Text style={[tableStyles.webCell, { textAlign: 'right' }]}>{rank}</Text>
              </View>
              <View style={tableStyles.colAbc}>
                <Text style={[tableStyles.webCell, { color: abcClassColor(abc.abcClass), fontWeight: '800', textAlign: 'center' }]}>
                  {abc.abcClass}
                </Text>
              </View>
              <View style={tableStyles.colProduct}>
                <Text style={tableStyles.webCell} numberOfLines={2}>
                  {label} · {typeLabel}
                </Text>
              </View>
              <View style={tableStyles.colSku}>
                <Text style={tableStyles.webCell} numberOfLines={2}>
                  {row.sku ?? '—'}
                </Text>
              </View>
              <View style={tableStyles.colQty}>
                <Text style={[tableStyles.webCell, { textAlign: 'right' }]}>{formatQty(row.quantity, row.unitOfMeasure)}</Text>
              </View>
              <View style={tableStyles.colMoney}>
                <Text style={[tableStyles.webCell, { textAlign: 'right', fontWeight: '800' }]}>{formatMoney(row.lineTotal)}</Text>
              </View>
              <View style={tableStyles.colPct}>
                <Text style={[tableStyles.webCell, { textAlign: 'right' }]}>{formatPct1(pct$)}</Text>
              </View>
              <View style={tableStyles.colPct}>
                <Text style={[tableStyles.webCell, { textAlign: 'right' }]}>{formatPct1(pctU)}</Text>
              </View>
              <View style={tableStyles.colAvg}>
                <Text style={[tableStyles.webCell, { textAlign: 'right' }]}>
                  {avg != null ? formatMoney(avg) : '—'}
                </Text>
              </View>
              <View style={tableStyles.colDetail}>
                {/* La fila entera es el botón — el texto queda como affordance visual. */}
                <Text
                  style={reportStyles.reportsDenseListDetalleBtnText}
                  testID="informes.ventas.detalle"
                >
                  Detalle
                </Text>
              </View>
            </Pressable>
          );
      })}
    </>
  );

  if (needsHorizontalScroll) {
    return (
      <View style={tableStyles.tableOuter}>
        <ScrollView horizontal showsHorizontalScrollIndicator accessibilityLabel="Tabla ventas por ítem">
          <View style={{ width: tableCanvasWidth, minWidth: TABLE_CONTENT_MIN_WIDTH }}>{tableInner}</View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={tableStyles.tableOuter}>
      <View style={{ width: '100%', minWidth: TABLE_CONTENT_MIN_WIDTH, maxWidth: '100%', alignSelf: 'stretch' }}>
        {tableInner}
      </View>
    </View>
  );
}

function NativeFieldRow({ label, value }: { label: string; value: string }) {
  return (
    <View
      style={[
        reportStyles.reportsDenseListFieldRow,
        { paddingVertical: 7, marginBottom: 2, alignItems: 'center' },
      ]}
    >
      <Text style={[reportStyles.reportsDenseListFieldLabel, { fontSize: 13 }]}>{label}:</Text>
      <Text style={[reportStyles.reportsDenseListFieldValue, { fontSize: 13, fontWeight: '600' }]} numberOfLines={3}>
        {value}
      </Text>
    </View>
  );
}

type ProductDetailModalPayload = {
  row: BranchSalesByProductRow;
  rank: number;
};

function SalesByProductNativeCards({
  rows,
  pageIndex,
  pageSize,
  sumLineTotal,
  sumQuantity: _sumQuantity,
  daysInRange,
  filterCategory,
  abcCumulativeBefore = 0,
  onOpenDetail,
}: LayoutProps) {
  const abcStartPct = sumLineTotal > 0 ? (abcCumulativeBefore / sumLineTotal) * 100 : 0;
  const abcSeries = computeAbcSeries(rows, sumLineTotal, abcStartPct);

  return (
    <View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.sm, paddingHorizontal: spacing.xs }}>
        <Ionicons name="information-circle-outline" size={14} color={ticketColors.ink3} />
        <Text style={{ ...typography.caption, color: ticketColors.ink3 }}>
          ABC: A = 80% del importe, B = hasta 95%, C = resto.
        </Text>
      </View>
      {rows.map((row, idx) => {
        const cardKey = `${row.productId}-${pageIndex * pageSize + idx}`;
        const rank = pageIndex * pageSize + idx + 1;
        const abc = abcSeries[idx];
        const upd = unitsPerDay(row.quantity, daysInRange);
        const showCat = showCategoryBadgeInListing(filterCategory, row.categoryId);
        const catDisplay = showCat ? getCategoryDisplayLabel(row.categoryId) : null;
        const typeLabel = row.itemType === 'service' ? 'Servicio' : 'Producto';

        const a11y = [
          `Puesto ${rank}, ${displayItemLabel(row)}.`,
          `Clase ABC ${abc.abcClass}.`,
          catDisplay ? `Categoría ${catDisplay}.` : '',
          `Tipo ${typeLabel}.`,
          `${formatQty(row.quantity, row.unitOfMeasure)} vendidas.`,
          upd != null ? `Promedio diario ${formatQty(upd, row.unitOfMeasure)} por día.` : '',
          `${formatMoney0(row.lineTotal)} pesos en el período.`,
          'El botón Detalle abre un panel con código, participación y precio medio.',
        ]
          .filter(Boolean)
          .join(' ');

        const openDetail = () => onOpenDetail(row, rank);

        return (
          <View key={cardKey} accessible accessibilityLabel={a11y}>
            <ReportsDenseListCard>
              <View style={reportStyles.reportsDenseListCardHeader}>
                <View style={reportStyles.reportsDenseListRankBadge}>
                  <Text style={reportStyles.reportsDenseListRankText} numberOfLines={1}>
                    {rank}
                  </Text>
                </View>
                <View
                  style={[
                    reportStyles.reportsDenseListRankBadge,
                    { marginLeft: spacing.xs, backgroundColor: 'transparent', borderColor: abcClassColor(abc.abcClass) },
                  ]}
                >
                  <Text style={[reportStyles.reportsDenseListRankText, { color: abcClassColor(abc.abcClass) }]} numberOfLines={1}>
                    {abc.abcClass}
                  </Text>
                </View>
                <View style={reportStyles.reportsDenseListTitleBlock}>
                  <Text style={reportStyles.reportsDenseListTitle} numberOfLines={2}>
                    {displayItemLabel(row)}
                  </Text>
                  <Text style={reportStyles.reportsDenseListMeta} numberOfLines={1}>
                    {catDisplay ? `${catDisplay} · ${typeLabel}` : typeLabel}
                  </Text>
                </View>
                <TouchableOpacity
                  style={reportStyles.reportsDenseListDetalleBtn}
                  onPress={openDetail}
                  accessibilityLabel="Detalle"
                  accessibilityRole="button"
                  accessibilityHint="Abre un panel con categoría, código, participación y precio medio"
                  activeOpacity={0.7}
                  testID="informes.ventas.detalle"
                >
                  <Text style={reportStyles.reportsDenseListDetalleBtnText}>Detalle</Text>
                </TouchableOpacity>
              </View>

              <View style={reportStyles.reportsDenseListMetricsRow}>
                <View style={reportStyles.reportsDenseListMetricCell}>
                  <Text style={reportStyles.reportsDenseListMetricValue}>{formatQty(row.quantity, row.unitOfMeasure)}</Text>
                  <Text style={reportStyles.reportsDenseListMetricLabel}>Vendidas</Text>
                </View>
                <View style={reportStyles.reportsDenseListMetricGutter} />
                <View style={reportStyles.reportsDenseListMetricCell}>
                  <Text style={reportStyles.reportsDenseListMetricValue}>
                    {upd != null ? formatQty(upd, row.unitOfMeasure) : '—'}
                  </Text>
                  <Text style={reportStyles.reportsDenseListMetricLabel}>Por día</Text>
                </View>
                <View style={reportStyles.reportsDenseListMetricGutter} />
                <View style={reportStyles.reportsDenseListMetricsMoneyWrap}>
                  <Text
                    style={reportStyles.reportsDenseListMoney}
                    numberOfLines={1}
                    adjustsFontSizeToFit
                    minimumFontScale={0.85}
                  >
                    {formatMoney(row.lineTotal)}
                  </Text>
                  <Text style={reportStyles.reportsDenseListMetricLabel} numberOfLines={1}>
                    Importe
                  </Text>
                </View>
              </View>
            </ReportsDenseListCard>
          </View>
        );
      })}
    </View>
  );
}

/** Tabla ancha: estilos alineados con CustomerTable. */
const tableStyles = StyleSheet.create({
  tableOuter: {
    width: '100%',
    alignSelf: 'stretch',
    borderRadius: SURFACE_CARD_RADIUS_CONTENT,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  webRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 44,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    gap: spacing.sm,
  },
  webRowHover: {
    backgroundColor: ticketColors.brandSoft,
  },
  webHeader: {
    minHeight: 40,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.rule,
  },
  webHeaderText: {
    ...typography.caption,
    fontWeight: '900',
    color: ticketColors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.35,
    lineHeight: 16,
    textAlign: 'right',
  },
  webCell: {
    ...typography.body,
    color: ticketColors.ink,
    fontSize: 13,
    lineHeight: 20,
  },
  colRank: { width: 28 },
  colAbc: { width: 40 },
  colProduct: { flex: 2, flexShrink: 1, minWidth: 150 },
  colSku: { flex: 1.25, flexShrink: 1, minWidth: 140, maxWidth: 280 },
  colQty: { width: 64, flexShrink: 0, textAlign: 'right' },
  colMoney: { width: 96, flexShrink: 0, textAlign: 'right', fontWeight: '800' },
  colPct: { width: 84, flexShrink: 0, textAlign: 'right' },
  colAvg: { width: 80, flexShrink: 0, textAlign: 'right' },
  colDetail: { width: 60, flexShrink: 0, alignItems: 'flex-end' },
  webHeaderSortActive: {
    color: ticketColors.gold,
  },
});
